import React, { useEffect, useRef } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import LinearGradient from "react-native-linear-gradient";

export type ApprovalConfirmVariant =
  | "approve"
  | "reject"
  | "activate"
  | "deactivate"
  | "warning";

type ApprovalConfirmModalProps = {
  visible: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ApprovalConfirmVariant;
};

const VARIANT_THEME: Record<
  ApprovalConfirmVariant,
  {
    icon: string;
    iconColor: string;
    iconBg: string;
    confirmStart: string;
    confirmEnd: string;
    accent: string;
  }
> = {
  approve: {
    icon: "check-decagram-outline",
    iconColor: "#166534",
    iconBg: "#DCFCE7",
    confirmStart: "#16A34A",
    confirmEnd: "#15803D",
    accent: "#16A34A",
  },
  reject: {
    icon: "close-octagon-outline",
    iconColor: "#B91C1C",
    iconBg: "#FEE2E2",
    confirmStart: "#DC2626",
    confirmEnd: "#B91C1C",
    accent: "#DC2626",
  },
  activate: {
    icon: "account-check-outline",
    iconColor: "#166534",
    iconBg: "#DCFCE7",
    confirmStart: "#16A34A",
    confirmEnd: "#15803D",
    accent: "#16A34A",
  },
  deactivate: {
    icon: "account-cancel-outline",
    iconColor: "#9A3412",
    iconBg: "#FFEDD5",
    confirmStart: "#F97316",
    confirmEnd: "#EA580C",
    accent: "#F97316",
  },
  warning: {
    icon: "alert-rhombus-outline",
    iconColor: "#C2410C",
    iconBg: "#FFEDD5",
    confirmStart: "#F97316",
    confirmEnd: "#EA580C",
    accent: "#F97316",
  },
};

export default function ApprovalConfirmModal({
  visible,
  title,
  message,
  onClose,
  onConfirm,
  loading = false,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "approve",
}: ApprovalConfirmModalProps) {
  const scaleAnim = useRef(new Animated.Value(0.92)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const theme = VARIANT_THEME[variant];

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 260,
          easing: Easing.out(Easing.back(1.1)),
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      opacityAnim.setValue(0);
      scaleAnim.setValue(0.92);
    }
  }, [opacityAnim, scaleAnim, visible]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={loading ? undefined : onClose}
          disabled={loading}
        />

        <Animated.View
          style={[
            styles.modalCard,
            {
              opacity: opacityAnim,
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          <LinearGradient colors={["#FFF7ED", "#FFFBEB"]} style={styles.heroPanel}>
            <View style={styles.heroBadge}>
              <MaterialCommunityIcons name="hands-pray" size={14} color="#C2410C" />
              <Text style={styles.heroBadgeText}>Hare Krishna</Text>
            </View>

            <View style={[styles.iconCircle, { backgroundColor: theme.iconBg }]}>
              <MaterialCommunityIcons
                name={theme.icon}
                size={32}
                color={theme.iconColor}
              />
            </View>

            <Text style={styles.title}>{title}</Text>
            <Text style={styles.message}>{message}</Text>

            <View style={[styles.accentLine, { backgroundColor: theme.accent }]} />
          </LinearGradient>

          <View style={styles.buttonRow}>
            <Pressable
              style={({ pressed }) => [
                styles.secondaryBtn,
                pressed && !loading ? styles.pressed : null,
              ]}
              onPress={onClose}
              disabled={loading}
            >
              <Text style={styles.secondaryBtnText}>{cancelLabel}</Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.primaryBtnWrap,
                pressed && !loading ? styles.pressed : null,
                loading ? styles.disabledBtn : null,
              ]}
              onPress={onConfirm}
              disabled={loading}
            >
              <LinearGradient
                colors={[theme.confirmStart, theme.confirmEnd]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.primaryBtn}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <MaterialCommunityIcons name={theme.icon} size={18} color="#FFFFFF" />
                )}
                <Text style={styles.primaryBtnText}>
                  {loading ? "Please wait..." : confirmLabel}
                </Text>
              </LinearGradient>
            </Pressable>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(71, 25, 4, 0.34)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 380,
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    overflow: "hidden",
    shadowColor: "#7C2D12",
    shadowOpacity: 0.24,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 14 },
    elevation: 14,
  },
  heroPanel: {
    paddingHorizontal: 22,
    paddingTop: 20,
    paddingBottom: 18,
    alignItems: "center",
  },
  heroBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.88)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.25)",
    marginBottom: 16,
  },
  heroBadgeText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#9A3412",
    letterSpacing: 0.4,
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 14,
  },
  title: {
    fontSize: 22,
    fontWeight: "900",
    color: "#1F2937",
    textAlign: "center",
  },
  message: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 22,
    color: "#5B6472",
    textAlign: "center",
  },
  accentLine: {
    width: 72,
    height: 4,
    borderRadius: 999,
    marginTop: 18,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 18,
    paddingBottom: 18,
    paddingTop: 6,
  },
  secondaryBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#FED7AA",
    backgroundColor: "#FFF7ED",
    justifyContent: "center",
    alignItems: "center",
  },
  secondaryBtnText: {
    fontSize: 14,
    fontWeight: "800",
    color: "#9A3412",
  },
  primaryBtnWrap: {
    flex: 1,
    borderRadius: 14,
    overflow: "hidden",
  },
  primaryBtn: {
    minHeight: 48,
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
  },
  primaryBtnText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#FFFFFF",
  },
  pressed: {
    opacity: 0.92,
  },
  disabledBtn: {
    opacity: 0.7,
  },
});
